<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('indietro') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-shield-alt me-2 text-primary"></i>
            {{ $t('gestione_allergeni') || 'Gestione Allergeni con AI' }}
          </h1>
          <p class="sci-subtitle">Rilevamento e conformità automatica allergeni per il menu secondo Reg. UE 1169/2011</p>
        </div>
      </div>
      <div class="sci-header-right">
        <span class="sci-badge sci-badge-stats">
          <i class="fas fa-check-circle text-success me-1"></i>
          {{ countWithAllergens }} con allergeni / {{ piatti.length }} piatti
        </span>
      </div>
    </header>

    <!-- Main Content Bento Grid -->
    <main class="sci-bento-grid">
      <!-- Left Column: AI Control Studio -->
      <aside class="sci-card control-panel">
        <div class="sci-card-header">
          <h2 class="sci-card-title">
            <i class="fas fa-brain me-2 text-primary"></i>
            Parametri Generazione AI
          </h2>
          <span class="sci-caption">Scegli le lingue e avvia l'analisi semantica</span>
        </div>

        <div class="control-body">
          <!-- Language Selector Chips -->
          <div class="sci-form-group">
            <label class="sci-label">Lingue di Output:</label>
            <div class="lang-chips-grid">
              <label class="lang-chip" :class="{ active: selectedLangs.includes('it') }">
                <input type="checkbox" value="it" v-model="selectedLangs" class="visually-hidden">
                <span class="flag-icon">🇮🇹</span>
                <span class="lang-name">Italiano (IT)</span>
              </label>
              <label class="lang-chip" :class="{ active: selectedLangs.includes('en') }">
                <input type="checkbox" value="en" v-model="selectedLangs" class="visually-hidden">
                <span class="flag-icon">🇬🇧</span>
                <span class="lang-name">Inglese (EN)</span>
              </label>
              <label class="lang-chip" :class="{ active: selectedLangs.includes('es') }">
                <input type="checkbox" value="es" v-model="selectedLangs" class="visually-hidden">
                <span class="flag-icon">🇪🇸</span>
                <span class="lang-name">Spagnolo (ES)</span>
              </label>
            </div>
          </div>

          <!-- Selection Summary & Actions -->
          <div class="selection-card">
            <div class="selection-card-header">
              <span class="selection-count-badge">
                {{ selectedItems.length }} di {{ piatti.length }} piatti selezionati
              </span>
              <div class="selection-quick-btns">
                <button class="btn-subtle" @click="selectAll">{{ $t('seleziona_tutti') || 'Tutti' }}</button>
                <span class="divider">|</span>
                <button class="btn-subtle" @click="deselectAll">{{ $t('deseleziona_tutti') || 'Nessuno' }}</button>
              </div>
            </div>

            <button 
              class="sci-btn sci-btn-primary w-100 btn-generate-cta"
              :disabled="isLoading || selectedItems.length === 0 || selectedLangs.length === 0"
              @click="startGeneration"
            >
              <i class="fas" :class="isLoading ? 'fa-spinner fa-spin' : 'fa-magic'"></i>
              <span>
                {{ isLoading ? 'Analisi AI in corso...' : 'Genera Allergeni AI (' + selectedItems.length + ')' }}
              </span>
            </button>
          </div>

          <!-- Educational Info Card -->
          <div class="ai-info-box">
            <div class="info-header">
              <i class="fas fa-info-circle text-info"></i>
              <span class="info-title">Come funziona l'AI</span>
            </div>
            <p class="info-text">
              L'intelligenza artificiale analizza il titolo, la descrizione e gli ingredienti mappati di ciascun piatto, identificando automaticamente i 14 allergeni normati e traducendoli nelle lingue selezionate.
            </p>
          </div>
        </div>
      </aside>

      <!-- Right Column: Dishes List -->
      <section class="sci-card dishes-panel">
        <div class="sci-card-header dishes-header">
          <div class="dishes-search-wrap">
            <i class="fas fa-search search-icon"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Filtra per nome piatto o portata..."
              class="sci-search-input"
            />
            <button 
              v-if="searchQuery" 
              class="clear-search-btn" 
              @click="searchQuery = ''"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="category-filter-pills" v-if="categories.length > 1">
            <button 
              class="category-pill" 
              :class="{ active: selectedCategory === '' }"
              @click="selectedCategory = ''"
            >
              Tutte ({{ piatti.length }})
            </button>
            <button 
              v-for="cat in categories" 
              :key="cat"
              class="category-pill" 
              :class="{ active: selectedCategory === cat }"
              @click="selectedCategory = cat"
            >
              {{ cat }}
            </button>
          </div>
        </div>

        <div class="table-responsive">
          <table class="sci-table">
            <thead>
              <tr>
                <th style="width: 48px; text-align: center;">
                  <input 
                    type="checkbox" 
                    @change="toggleAll($event)" 
                    :checked="isAllSelected"
                    class="sci-checkbox"
                  />
                </th>
                <th style="width: 140px;">Portata</th>
                <th>Piatto & Dettagli</th>
                <th style="width: 160px; text-align: right;">Stato Allergeni</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="item in filteredPiatti" 
                :key="item.nome" 
                class="sci-table-row"
                :class="{ 'row-selected': selectedItems.includes(item.nome) }"
                @click="toggleSelection(item.nome)"
              >
                <!-- Checkbox -->
                <td style="text-align: center;" @click.stop>
                  <input 
                    type="checkbox" 
                    :value="item.nome" 
                    v-model="selectedItems"
                    class="sci-checkbox"
                  />
                </td>

                <!-- Category -->
                <td>
                  <span class="badge-portata">{{ item.portata || 'Altro' }}</span>
                </td>

                <!-- Dish Name & Ingredients -->
                <td>
                  <div class="dish-info-cell">
                    <span class="dish-title">{{ item.nome }}</span>
                    <span v-if="item.descrizione" class="dish-desc">{{ item.descrizione }}</span>
                    <!-- Detected Allergens Preview -->
                    <div v-if="item.allergeni" class="allergen-tags-row">
                      <span class="allergen-pill" v-for="(all, idx) in parseAllergens(item.allergeni)" :key="idx">
                        {{ all }}
                      </span>
                    </div>
                  </div>
                </td>

                <!-- Status -->
                <td style="text-align: right;">
                  <span v-if="item.allergeni" class="status-badge-present">
                    <i class="fas fa-check-circle me-1"></i> Rilevati
                  </span>
                  <span v-else class="status-badge-missing">
                    <i class="fas fa-clock me-1"></i> Da generare
                  </span>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredPiatti.length === 0">
                <td colspan="4" class="sci-empty-state">
                  <div class="empty-content">
                    <div class="empty-icon-halo">
                      <i class="fas fa-utensils"></i>
                    </div>
                    <h3 class="empty-title">Nessun piatto trovato</h3>
                    <p class="empty-desc">
                      {{ searchQuery ? 'Nessun piatto corrisponde ai filtri di ricerca selezionati.' : 'Non ci sono piatti registrati nel menu.' }}
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'Allergeni',
  data() {
    return {
      piatti: [],
      selectedItems: [],
      selectedLangs: ['it', 'en', 'es'],
      searchQuery: '',
      selectedCategory: '',
      isLoading: false,
      store
    }
  },
  computed: {
    categories() {
      const cats = new Set();
      this.piatti.forEach(p => {
        if (p.portata) cats.add(p.portata);
      });
      return Array.from(cats);
    },
    countWithAllergens() {
      return this.piatti.filter(p => !!p.allergeni).length;
    },
    filteredPiatti() {
      let list = this.piatti;
      if (this.selectedCategory) {
        list = list.filter(p => p.portata === this.selectedCategory);
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(p => 
          (p.nome && p.nome.toLowerCase().includes(q)) ||
          (p.portata && p.portata.toLowerCase().includes(q)) ||
          (p.descrizione && p.descrizione.toLowerCase().includes(q))
        );
      }
      return list;
    },
    isAllSelected() {
      return this.filteredPiatti.length > 0 && this.selectedItems.length >= this.filteredPiatti.length;
    }
  },
  methods: {
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    async fetchData() {
      try {
        const config = {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        };
        const response = await this.axios.post(this.store.path + 'displayMenu', config);
        this.piatti = response.data.messaggio || [];
      } catch (error) {
        console.error("Errore caricamento menu:", error);
      }
    },
    toggleAll(e) {
      if (e.target.checked) {
        this.selectedItems = this.filteredPiatti.map(p => p.nome);
      } else {
        this.selectedItems = [];
      }
    },
    selectAll() {
      this.selectedItems = this.filteredPiatti.map(p => p.nome);
    },
    deselectAll() {
      this.selectedItems = [];
    },
    toggleSelection(nome) {
      const index = this.selectedItems.indexOf(nome);
      if (index === -1) {
        this.selectedItems.push(nome);
      } else {
        this.selectedItems.splice(index, 1);
      }
    },
    parseAllergens(allergeni) {
      if (!allergeni) return [];
      if (Array.isArray(allergeni)) return allergeni;
      if (typeof allergeni === 'string') {
        try {
          const parsed = JSON.parse(allergeni);
          if (Array.isArray(parsed)) return parsed;
        } catch {
          return allergeni.split(',').map(s => s.trim()).filter(Boolean);
        }
      }
      return [];
    },
    async startGeneration() {
      this.isLoading = true;
      try {
        const response = await this.axios.post(this.store.path + 'generate-allergens', {
          dishes: this.selectedItems,
          languages: this.selectedLangs
        }, {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        });

        alert(response.data.messaggio || 'Allergeni generati con successo!');
        await this.fetchData();
        this.selectedItems = [];
      } catch (error) {
        console.error("Errore generazione allergeni:", error);
        alert("Errore durante la generazione degli allergeni con AI.");
      } finally {
        this.isLoading = false;
      }
    }
  },
  mounted() {
    this.fetchData();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 50px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.sci-badge-stats {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* Bento Grid */
.sci-bento-grid {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
  max-width: 1400px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
}

@media (max-width: 960px) {
  .sci-bento-grid {
    grid-template-columns: 1fr;
  }
}

/* Bento Cards */
.sci-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.sci-card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
}

.sci-card-title {
  margin: 0 0 4px 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.sci-caption {
  font-size: 0.85rem;
  color: #64748b;
}

/* Left Panel */
.control-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sci-form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sci-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
}

.lang-chips-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lang-chip {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 10px 16px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.lang-chip:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.lang-chip.active {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1d4ed8;
  font-weight: 600;
}

.flag-icon {
  font-size: 1.25rem;
}

.lang-name {
  font-size: 0.95rem;
}

/* Selection Summary Card */
.selection-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.selection-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selection-count-badge {
  font-size: 0.88rem;
  font-weight: 600;
  color: #334155;
}

.selection-quick-btns {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-subtle {
  background: none;
  border: none;
  color: #2563eb;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.btn-subtle:hover {
  text-decoration: underline;
}

.divider {
  color: #cbd5e1;
  font-size: 0.85rem;
}

.btn-generate-cta {
  min-height: 48px;
  font-size: 1rem;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
}

.btn-generate-cta:hover:not(:disabled) {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  transform: translateY(-1px);
}

.btn-generate-cta:disabled {
  background: #cbd5e1;
  color: #94a3b8;
  box-shadow: none;
  cursor: not-allowed;
}

/* AI Info Box */
.ai-info-box {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 16px;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.info-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #166534;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.info-text {
  margin: 0;
  font-size: 0.85rem;
  color: #15803d;
  line-height: 1.45;
}

/* Right Panel: Dishes List */
.dishes-header {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.dishes-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 0.95rem;
}

.sci-search-input {
  width: 100%;
  min-height: 44px;
  padding: 8px 40px 8px 40px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #0f172a;
}

.sci-search-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.clear-search-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 6px;
}

.clear-search-btn:hover {
  color: #ef4444;
}

/* Category Filter Pills */
.category-filter-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-pill {
  min-height: 34px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-pill:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.category-pill.active {
  background: #334155;
  color: #ffffff;
  border-color: #334155;
}

/* Table */
.sci-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}

.sci-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 14px 20px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sci-table-row {
  cursor: pointer;
}

.sci-table-row td {
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  transition: background 0.15s ease;
}

.sci-table-row:hover td {
  background: #f8fafc;
}

.sci-table-row.row-selected td {
  background: #eff6ff;
}

.sci-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  accent-color: #2563eb;
  cursor: pointer;
}

.badge-portata {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 600;
}

.dish-info-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dish-title {
  font-size: 0.98rem;
  font-weight: 700;
  color: #0f172a;
}

.dish-desc {
  font-size: 0.85rem;
  color: #64748b;
  max-width: 500px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.allergen-tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.allergen-pill {
  font-size: 0.72rem;
  font-weight: 600;
  background: #fee2e2;
  color: #b91c1c;
  padding: 2px 8px;
  border-radius: 12px;
}

/* Statuses */
.status-badge-present {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #ecfdf5;
  color: #059669;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-badge-missing {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #fffbeb;
  color: #b45309;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
}

.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

/* Empty State */
.sci-empty-state {
  padding: 60px 24px;
  text-align: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 400px;
  margin: 0 auto;
}

.empty-icon-halo {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.empty-title {
  margin: 0 0 6px 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  margin: 0;
  font-size: 0.9rem;
  color: #64748b;
}
</style>
